import React, { useState, useEffect } from "react";
import { DatePicker, Form, TimePicker, Spin, Select, Input } from "antd"; // Import Select for branch dropdown
import dayjs from "dayjs";
import { useGetDeliveryModesQuery } from "@/services/api/deliveryMode/deliveryModeApi";
import GooglePlacesAutocomplete from "@/components/forms/GooglePlacesAutocomplete";
import {
  CarOutlined, // Delivery Icon
  ShopOutlined, // Pickup Icon
} from "@ant-design/icons"; // Import Ant Design Icons

const DeliverySection = ({
  // deliveryType is no longer directly needed as a prop for external control,
  // it will be derived from selectedDeliveryMode internally.
  // setDeliveryType is also no longer needed externally.
  form,
  locationLoading,
  locationOptions,
}) => {
  const { data: deliveryModes, isLoading: isDeliveryModesLoading } =
    useGetDeliveryModesQuery();

  // State to hold the ID of the currently selected delivery mode
  const [selectedDeliveryModeId, setSelectedDeliveryModeId] = useState(null);

  // Derive the currently selected delivery mode object
  const selectedDeliveryMode = deliveryModes?.find(
    (mode) => mode.id === selectedDeliveryModeId,
  );

  // Set initial delivery mode when data loads
  useEffect(() => {
    if (!selectedDeliveryModeId && deliveryModes && deliveryModes.length > 0) {
      // Set the first delivery mode as default or a specific one if needed
      setSelectedDeliveryModeId(deliveryModes[0].id);
      form.setFieldsValue({ deliveryModeId: deliveryModes[0].id });
    }
  }, [deliveryModes, selectedDeliveryModeId, form]);

  const handleDeliveryModeChange = (modeId) => {
    setSelectedDeliveryModeId(modeId);
    form.setFieldsValue({ deliveryModeId: modeId });
  };

  if (isDeliveryModesLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spin size="large" />
      </div>
    );
  }

  if (!deliveryModes || deliveryModes.length === 0) {
    return (
      <div className="text-center p-8 text-gray-500">
        No delivery modes available.
      </div>
    );
  }

  // Determine if the selected mode is a 'delivery' or 'pickup' type
  // You might need to adjust these checks based on the actual properties in your deliveryModes API response
  // For example, if your API returns 'type: "delivery"' or 'type: "pickup"'
  const isSelectedModeDelivery = selectedDeliveryMode?.name
    .toLowerCase()
    .includes("delivery");
  const isSelectedModePickup = selectedDeliveryMode?.name
    .toLowerCase()
    .includes("pickup");

  return (
    <div>
      <Form form={form} layout="vertical">
        {/* Delivery Mode Selection (Dynamic Buttons) */}
        <Form.Item
          name="deliveryModeId" // This will store the selected mode's ID
          noStyle // Hide Ant Design's default label/wrapper for this item
          rules={[{ required: true, message: "Please select a delivery mode" }]}
        >
          {/* This hidden Input ensures Ant Design Form captures the value */}
          <Input type="hidden" />
        </Form.Item>

        <div className="grid grid-cols-2 gap-4 mb-6">
          {deliveryModes.map((mode) => (
            <button
              key={mode.id}
              type="button"
              onClick={() => handleDeliveryModeChange(mode.id)}
              className={`p-4 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                selectedDeliveryModeId === mode.id
                  ? "border-orange-500 bg-orange-50 text-orange-700"
                  : "border-gray-200 bg-gray-50 text-gray-500"
              }`}
            >
              <span
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedDeliveryModeId === mode.id
                    ? "border-orange-500"
                    : "border-gray-300"
                }`}
              >
                {selectedDeliveryModeId === mode.id && (
                  <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                )}
              </span>
              <span className="font-medium">{mode.name}</span>
              {/* Optional: Add icons based on mode name or a specific property */}
              {mode.name.toLowerCase().includes("delivery") ? (
                <CarOutlined className="w-5 h-5" />
              ) : (
                <ShopOutlined className="w-5 h-5" />
              )}
            </button>
          ))}
        </div>

        {/* Conditional Form Fields based on selected delivery mode */}
        {isSelectedModeDelivery && (
          <>
            <Form.Item
              label={
                <span>
                  Delivery Date <span className="text-red-500">*</span>
                </span>
              }
              name="deliveryDate"
              rules={[
                {
                  required: true,
                  message: "Please select delivery date",
                },
              ]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD-MM-YYYY"
                placeholder="Select delivery date"
                size="large"
                disabledDate={(current) =>
                  current && current < dayjs().startOf("day")
                }
              />
            </Form.Item>
            <Form.Item
              label={
                <span>
                  Delivery Time <span className="text-red-500">*</span>
                </span>
              }
              name="deliveryTime"
              rules={[
                { required: true, message: "Please select delivery time" },
              ]}
              getValueProps={(value) => ({
                value: value ? dayjs(value, "H:mm") : null,
              })}
              normalize={(value) => {
                if (!value) return null;
                const hour = value.hour();
                const minute = value.minute().toString().padStart(2, "0");
                return `${hour}:${minute}`;
              }}
            >
              <TimePicker
                style={{ width: "100%" }}
                format="H:mm"
                placeholder="Select delivery time"
                size="large"
              />
            </Form.Item>
            <Form.Item
              label={
                <span>
                  Delivery Location <span className="text-red-500">*</span>
                </span>
              }
              name="deliveryLocation"
              getValueFromEvent={(value) => value}
              rules={[
                {
                  required: true,
                  message: "Please enter delivery location",
                },
              ]}
            >
              <GooglePlacesAutocomplete
                placeholder="--Enter Delivery Address--"
                className="p-3 rounded-lg text-base"
                onPlaceSelect={(place) =>
                  form.setFieldsValue({ deliveryLocation: place.address })
                }
              />
            </Form.Item>
          </>
        )}

        {isSelectedModePickup && (
          <>
            <Form.Item
              label={
                <span>
                  Pickup Date <span className="text-red-500">*</span>
                </span>
              }
              name="pickupDate"
              rules={[{ required: true, message: "Please select pickup date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD-MM-YYYY"
                placeholder="Select pickup date"
                size="large"
                disabledDate={(current) =>
                  current && current < dayjs().startOf("day")
                }
              />
            </Form.Item>
            <Form.Item
              label={
                <span>
                  Pickup Time <span className="text-red-500">*</span>
                </span>
              }
              name="pickupTime"
              rules={[{ required: true, message: "Please select pickup time" }]}
              getValueProps={(value) => ({
                value: value ? dayjs(value, "H:mm") : null,
              })}
              normalize={(value) => {
                if (!value) return null;
                const hour = value.hour();
                const minute = value.minute().toString().padStart(2, "0");
                return `${hour}:${minute}`;
              }}
            >
              <TimePicker
                style={{ width: "100%" }}
                format="H:mm"
                placeholder="Select pickup time"
                size="large"
              />
            </Form.Item>
          </>
        )}

        {/* Instructions and Branch are common to both delivery and pickup based on your original code */}
        <Form.Item label="Instructions" name="instructions">
          <Input.TextArea // Using Ant Design TextArea
            className="w-full p-3 border border-gray-300 rounded-lg resize-none" // Tailwind classes
            rows={3}
            placeholder="--Enter Specific instructions the customer mentioned--"
            size="large"
          />
        </Form.Item>

        <Form.Item label="Branch" name="branch">
          <Select
            placeholder={
              locationLoading ? "Loading branches..." : "--Select Branch--"
            }
            size="large"
            loading={locationLoading}
            disabled={locationLoading}
            options={locationOptions}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
      </Form>
    </div>
  );
};

export default DeliverySection;
